import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMarketingAddon } from '../contexts/MarketingAddonContext';
import { useToast } from '../hooks/useToast';
import { api, apiFetch } from '../lib/api';
import { GlassCard } from '../components/GlassCard';
import {
  Sparkles, Download, Trash2, Calendar, Instagram,
  Facebook, Unlink, Link2, X, Check, ShoppingCart,
} from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL || '';
function fmtINR(n) { try { return '₹' + Math.round(n).toLocaleString('en-IN'); } catch { return '₹' + n; } }
function fmtDate(iso) { if (!iso) return ''; try { return new Date(iso).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; } }

const SUGGESTION_CHIPS = ['20% discount poster', 'Diwali sale', 'Grand opening', 'New product launch'];
const SIZES = [
  { value: '1024x1024', label: 'Square' },
  { value: '1024x1536', label: 'Portrait' },
  { value: '1536x1024', label: 'Landscape' },
];
const PACKS = [
  { id: 'small', credits: 25, price: 199 },
  { id: 'medium', credits: 100, price: 699 },
  { id: 'large', credits: 500, price: 2999 },
];

export default function BrandAssets() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { activated, remaining, quota, used, status, refresh: refreshAddon } = useMarketingAddon();

  const [assets, setAssets] = useState([]);
  const [social, setSocial] = useState(null);
  const [scheduled, setScheduled] = useState([]);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [connectOpen, setConnectOpen] = useState(null);
  const [newAssetId, setNewAssetId] = useState(null);

  // Guard: redirect if not activated
  useEffect(() => {
    if (activated === false && status !== null) {
      toast.show('Activate AI Marketing first', 'error');
      navigate('/home', { replace: true });
    }
  }, [activated, status, navigate, toast]);

  const loadAssets = useCallback(async () => {
    if (!token) return;
    try { const r = await api.listMarketingAssets(token); setAssets(r?.items || []); }
    catch { /* silent */ }
  }, [token]);

  const loadSocial = useCallback(async () => {
    if (!token) return;
    try { setSocial(await api.getSocialStatus(token)); } catch { /* silent */ }
  }, [token]);

  const loadScheduled = useCallback(async () => {
    if (!token) return;
    try { const r = await api.listScheduled(token, 'scheduled'); setScheduled(Array.isArray(r) ? r : r?.items || []); }
    catch { /* silent */ }
  }, [token]);

  useEffect(() => { loadAssets(); loadSocial(); loadScheduled(); }, [loadAssets, loadSocial, loadScheduled]);

  if (!activated) return null;

  const billingReset = status?.billing_cycle_start
    ? fmtDate(new Date(new Date(status.billing_cycle_start).getTime() + 30 * 86400000).toISOString())
    : '';

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="brand-assets-page">
      {/* A) Quota Banner */}
      <QuotaBanner used={used} quota={quota} remaining={remaining} resetDate={billingReset} onBuy={() => setCreditsOpen(true)} />

      {/* B) Creative Engine */}
      <CreativeEngine token={token} remaining={remaining} onBuy={() => setCreditsOpen(true)}
        onCreated={(a) => { setNewAssetId(a.asset_id); loadAssets(); refreshAddon(); }} />

      {/* C) Gallery */}
      <Gallery assets={assets} token={token} newAssetId={newAssetId}
        onDelete={loadAssets} onSchedule={setScheduleTarget} />

      {/* D) Social Connect */}
      <SocialConnect social={social} onConnect={setConnectOpen} onRefresh={loadSocial} token={token} />

      {/* E) Scheduled Posts */}
      <ScheduledPosts scheduled={scheduled} token={token} onRefresh={loadScheduled} />

      {/* Sheets/Modals */}
      {creditsOpen && (
        <BuyCreditsSheet token={token} onClose={() => setCreditsOpen(false)} onSuccess={() => { refreshAddon(); setCreditsOpen(false); }} />
      )}
      {scheduleTarget && (
        <ScheduleSheet asset={scheduleTarget} social={social} token={token}
          onClose={() => setScheduleTarget(null)} onSuccess={() => { loadScheduled(); setScheduleTarget(null); }} />
      )}
      {connectOpen && (
        <SocialConnectModal provider={connectOpen} token={token}
          onClose={() => setConnectOpen(null)} onSuccess={() => { loadSocial(); setConnectOpen(null); }} />
      )}
    </div>
  );
}

/* ── Quota Banner ── */
function QuotaBanner({ used, quota, remaining, resetDate, onBuy }) {
  const color = remaining > 10 ? 'teal' : remaining > 2 ? 'amber' : 'red';
  const bg = { teal: 'bg-teal-500/10 border-teal-500/20', amber: 'bg-amber-500/10 border-amber-500/20', red: 'bg-red-500/10 border-red-500/20' }[color];
  const txt = { teal: 'text-teal-400', amber: 'text-amber-400', red: 'text-red-400' }[color];
  return (
    <div className={`flex items-center justify-between gap-4 px-5 py-3 rounded-2xl border ${bg}`} data-testid="marketing-quota-banner">
      <p className={`text-sm font-bold ${txt}`}>
        <span className="tabular-nums">{used} / {quota}</span> designs used this month
        {resetDate && <span className="text-white/40 font-medium"> · Resets {resetDate}</span>}
      </p>
      {remaining <= 0 && (
        <button onClick={onBuy} className="px-4 py-2 rounded-xl bg-pink-600 text-white text-xs font-bold hover:bg-pink-700 transition-colors" data-testid="quota-buy-btn">
          Buy More Credits
        </button>
      )}
    </div>
  );
}

/* ── Creative Engine ── */
function CreativeEngine({ token, remaining, onBuy, onCreated }) {
  const toast = useToast();
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('1024x1024');
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!prompt.trim() || !token) return;
    setGenerating(true);
    try {
      const r = await api.generateMarketingDesign(token, { prompt: prompt.trim(), size });
      onCreated(r);
      toast.show(`Design created · ${r.quota?.remaining ?? '?'} remaining`, 'success');
      setPrompt('');
    } catch (e) {
      if (e.status === 429) { onBuy(); return; }
      toast.show(e.message || 'Generation failed', 'error');
    } finally { setGenerating(false); }
  };

  return (
    <GlassCard dark className="p-5 space-y-4" testId="creative-engine">
      <h3 className="text-white font-bold text-sm flex items-center gap-2"><Sparkles size={16} className="text-pink-400" /> Creative Engine</h3>
      <textarea
        data-testid="creative-prompt"
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        placeholder="Describe your design... e.g., '20% off Diwali sale poster for my bakery'"
        rows={3}
        className="w-full px-4 py-3 rounded-2xl bg-white/[0.05] border border-white/10 text-white text-sm placeholder:text-white/30 resize-none focus:outline-none focus:border-pink-500/40"
      />
      <div className="flex flex-wrap gap-1.5">
        {SUGGESTION_CHIPS.map(c => (
          <button key={c} onClick={() => setPrompt(c)} className="px-3 py-1.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-400 text-[11px] font-bold hover:bg-pink-500/20 transition-colors">
            {c}
          </button>
        ))}
      </div>
      <div className="flex gap-1.5">
        {SIZES.map(s => (
          <button key={s.value} onClick={() => setSize(s.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${size === s.value ? 'bg-white/10 text-white border border-white/20' : 'text-white/40 hover:text-white/60'}`}>
            {s.label}
          </button>
        ))}
      </div>
      {generating ? (
        <div className="flex items-center gap-3 py-4">
          <div className="w-5 h-5 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-white/50 text-sm font-medium">Creating your design...</span>
        </div>
      ) : remaining > 0 ? (
        <button data-testid="creative-generate-btn" onClick={generate} disabled={!prompt.trim()}
          className="px-6 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-pink-600 text-white font-bold text-sm hover:brightness-110 transition-all disabled:opacity-40">
          Generate Design
        </button>
      ) : (
        <button data-testid="creative-buy-btn" onClick={onBuy}
          className="px-6 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-pink-600 text-white font-bold text-sm hover:brightness-110 transition-all">
          Buy More Credits
        </button>
      )}
    </GlassCard>
  );
}

/* ── Gallery ── */
function Gallery({ assets, token, newAssetId, onDelete, onSchedule }) {
  const toast = useToast();
  const [deleting, setDeleting] = useState(null);

  const download = async (a) => {
    try {
      const res = await fetch(`${BASE}/api/marketing/assets/${a.asset_id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `design_${a.asset_id.slice(0, 8)}.png`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.show(e.message || 'Download failed', 'error'); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete this design?`)) return;
    setDeleting(a.asset_id);
    try {
      await api.deleteMarketingAsset(token, a.asset_id);
      toast.show('Design deleted', 'success');
      onDelete();
    } catch (e) { toast.show(e.message || 'Delete failed', 'error'); }
    finally { setDeleting(null); }
  };

  if (!assets.length) return null;
  return (
    <div>
      <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">Your Designs</h3>
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {assets.map(a => (
          <div key={a.asset_id} data-testid={`gallery-tile-${a.asset_id}`}
            className={`relative group rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] ${a.asset_id === newAssetId ? 'ring-2 ring-pink-500/50' : ''}`}>
            <img
              src={`${BASE}${a.image_url || a.thumbnail_url}`}
              alt={a.prompt}
              className="w-full aspect-square object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
              <button onClick={() => download(a)} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors" data-testid={`gallery-download-${a.asset_id}`}>
                <Download size={16} />
              </button>
              <button onClick={() => onSchedule(a)} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors" data-testid={`gallery-schedule-${a.asset_id}`}>
                <Calendar size={16} />
              </button>
              <button onClick={() => remove(a)} disabled={deleting === a.asset_id} className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50" data-testid={`gallery-delete-${a.asset_id}`}>
                <Trash2 size={16} />
              </button>
            </div>
            <div className="p-2">
              <p className="text-white/50 text-[10px] truncate">{a.prompt}</p>
              <p className="text-white/30 text-[9px]">{fmtDate(a.generated_at)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Social Connect ── */
function SocialConnect({ social, onConnect, onRefresh, token }) {
  const toast = useToast();
  const providers = [
    { key: 'instagram', icon: Instagram, label: 'Instagram', color: '#E1306C' },
    { key: 'facebook', icon: Facebook, label: 'Facebook', color: '#1877F2' },
  ];

  const disconnect = async (p) => {
    try {
      await api.socialDisconnect(token, { provider: p });
      toast.show(`${p} disconnected`, 'success');
      onRefresh();
    } catch (e) { toast.show(e.message || 'Disconnect failed', 'error'); }
  };

  if (!social) return null;
  return (
    <GlassCard dark className="p-5" testId="social-connect-card">
      <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-4">Social Accounts</h3>
      <div className="space-y-3">
        {providers.map(p => {
          const s = social[p.key];
          const connected = s?.connected;
          return (
            <div key={p.key} className="flex items-center gap-3">
              <p.icon size={20} style={{ color: p.color }} />
              <span className="text-white font-semibold text-sm flex-1">{p.label}</span>
              {connected ? (
                <>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[10px] font-bold border border-emerald-500/25">Connected</span>
                  <button onClick={() => disconnect(p.key)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors" data-testid={`social-disconnect-${p.key}`}>
                    <Unlink size={12} />
                  </button>
                </>
              ) : (
                <>
                  <span className="px-2 py-0.5 rounded-full bg-white/5 text-white/40 text-[10px] font-bold border border-white/10">Not Connected</span>
                  <button onClick={() => onConnect(p.key)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-white/10 hover:bg-white/15 transition-colors" data-testid={`social-connect-${p.key}`}>
                    <Link2 size={12} />
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
}

/* ── Scheduled Posts ── */
function ScheduledPosts({ scheduled, token, onRefresh }) {
  const toast = useToast();
  const cancel = async (id) => {
    try {
      await api.cancelScheduled(token, id);
      toast.show('Post cancelled', 'success');
      onRefresh();
    } catch (e) { toast.show(e.message || 'Cancel failed', 'error'); }
  };

  if (!scheduled.length) return null;
  return (
    <div>
      <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">Scheduled Posts</h3>
      <div className="space-y-2">
        {scheduled.map(s => (
          <GlassCard dark key={s.schedule_id || s.id} className="p-4 flex items-center gap-4" testId={`scheduled-${s.schedule_id || s.id}`}>
            {s.provider === 'instagram' ? <Instagram size={16} className="text-pink-400" /> : <Facebook size={16} className="text-blue-400" />}
            <div className="flex-1 min-w-0">
              <p className="text-white/70 text-xs truncate">{s.caption || '—'}</p>
              <p className="text-white/40 text-[10px]">{fmtDate(s.scheduled_at)}</p>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${s.status === 'scheduled' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25' : 'bg-white/5 text-white/40 border border-white/10'}`}>
              {s.status}
            </span>
            {s.status === 'scheduled' && (
              <button onClick={() => cancel(s.schedule_id || s.id)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors" data-testid={`scheduled-cancel-${s.schedule_id || s.id}`}>
                Cancel
              </button>
            )}
          </GlassCard>
        ))}
      </div>
    </div>
  );
}

/* ── Buy Credits Sheet ── */
function BuyCreditsSheet({ token, onClose, onSuccess }) {
  const toast = useToast();
  const [selected, setSelected] = useState('medium');
  const [busy, setBusy] = useState(false);
  const [bloom, setBloom] = useState(false);

  const buy = async () => {
    setBusy(true);
    try {
      const r = await api.buyMarketingCredits(token, { pack: selected });
      setBloom(true);
      const pack = PACKS.find(p => p.id === selected);
      toast.show(`${pack?.credits || ''} credits added · ${r.addon?.remaining ?? '?'} total`, 'success');
      setTimeout(onSuccess, 900);
    } catch (e) { toast.show(e.message || 'Purchase failed', 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0B1220] border border-white/10 rounded-3xl max-w-lg w-full mx-4 p-6 shadow-2xl" data-testid="buy-credits-sheet">
        {bloom && (
          <div className="absolute inset-0 flex items-center justify-center z-[60] pointer-events-none ai-bloom-overlay" data-testid="credits-bloom">
            <div className="ai-bloom-glow" />
            <div className="ai-bloom-circle">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="ai-bloom-check">
                <path d="M12 24L21 33L36 15" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="ai-bloom-checkpath" />
              </svg>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-extrabold text-lg flex items-center gap-2"><ShoppingCart size={18} className="text-pink-400" /> Buy More Credits</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 text-white/60 transition-colors"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          {PACKS.map(p => (
            <button key={p.id} onClick={() => setSelected(p.id)} data-testid={`pack-${p.id}`}
              className={`p-4 rounded-2xl border text-center transition-all ${selected === p.id ? 'bg-pink-500/15 border-pink-500/30 ring-1 ring-pink-500/20' : 'bg-white/[0.03] border-white/10 hover:border-white/20'}`}>
              <p className="text-white font-extrabold text-2xl tabular-nums">{p.credits}</p>
              <p className="text-white/40 text-[10px] uppercase font-bold">credits</p>
              <p className="text-pink-400 font-bold text-sm mt-2 tabular-nums">{fmtINR(p.price)}</p>
            </button>
          ))}
        </div>
        <p className="text-white/30 text-xs italic mb-4">Simulated purchase — no card charged.</p>
        <button data-testid="credits-confirm-btn" onClick={buy} disabled={busy}
          className="w-full h-12 rounded-xl bg-gradient-to-r from-pink-500 to-pink-600 text-white font-bold text-sm hover:brightness-110 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check size={16} />}
          {busy ? 'Processing...' : 'Confirm Purchase'}
        </button>
      </div>
    </div>
  );
}

/* ── Schedule Sheet ── */
function ScheduleSheet({ asset, social, token, onClose, onSuccess }) {
  const toast = useToast();
  const [provider, setProvider] = useState('instagram');
  const [caption, setCaption] = useState(asset?.prompt || '');
  const [dateTime, setDateTime] = useState('');
  const [busy, setBusy] = useState(false);

  const schedule = async () => {
    if (!dateTime) { toast.show('Pick a date/time', 'error'); return; }
    setBusy(true);
    try {
      await api.scheduleAsset(token, asset.asset_id, { provider, scheduled_at: new Date(dateTime).toISOString(), caption });
      toast.show('Post scheduled', 'success');
      onSuccess();
    } catch (e) { toast.show(e.message || 'Schedule failed', 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0B1220] border border-white/10 rounded-3xl max-w-md w-full mx-4 p-6 shadow-2xl" data-testid="schedule-sheet">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-extrabold text-lg">Schedule Post</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 text-white/60 transition-colors"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div className="flex gap-2">
            {['instagram', 'facebook'].map(p => (
              <button key={p} onClick={() => setProvider(p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold capitalize transition-all ${provider === p ? 'bg-pink-500/15 text-pink-400 border border-pink-500/25' : 'bg-white/5 text-white/40 border border-white/10'}`}>
                {p === 'instagram' ? <Instagram size={14} /> : <Facebook size={14} />} {p}
              </button>
            ))}
          </div>
          <input type="datetime-local" value={dateTime} onChange={e => setDateTime(e.target.value)}
            className="w-full h-11 px-4 rounded-xl bg-white/[0.05] border border-white/10 text-white text-sm focus:outline-none focus:border-pink-500/40" data-testid="schedule-datetime" />
          <textarea value={caption} onChange={e => setCaption(e.target.value.slice(0, 2200))} rows={3}
            placeholder="Caption..." maxLength={2200}
            className="w-full px-4 py-3 rounded-xl bg-white/[0.05] border border-white/10 text-white text-sm placeholder:text-white/30 resize-none focus:outline-none focus:border-pink-500/40" data-testid="schedule-caption" />
          <p className="text-white/30 text-[10px] text-right">{caption.length}/2200</p>
          <button data-testid="schedule-confirm-btn" onClick={schedule} disabled={busy}
            className="w-full h-11 rounded-xl bg-gradient-to-r from-pink-500 to-pink-600 text-white font-bold text-sm hover:brightness-110 transition-all disabled:opacity-50">
            {busy ? 'Scheduling...' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Social Connect Modal ── */
function SocialConnectModal({ provider, token, onClose, onSuccess }) {
  const toast = useToast();
  const [accessToken, setAccessToken] = useState('');
  const [pageId, setPageId] = useState('');
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    if (!accessToken.trim()) { toast.show('Enter an access token', 'error'); return; }
    setBusy(true);
    try {
      await api.socialConnect(token, { provider, access_token: accessToken.trim(), page_id: pageId.trim() || undefined });
      toast.show(`${provider} connected`, 'success');
      onSuccess();
    } catch (e) { toast.show(e.message || 'Connect failed', 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0B1220] border border-white/10 rounded-3xl max-w-sm w-full mx-4 p-6 shadow-2xl" data-testid="social-connect-modal">
        <h3 className="text-white font-extrabold text-lg mb-4 capitalize">Connect {provider}</h3>
        <div className="space-y-3">
          <input value={accessToken} onChange={e => setAccessToken(e.target.value)} placeholder="Access Token"
            className="w-full h-11 px-4 rounded-xl bg-white/[0.05] border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-pink-500/40" data-testid="social-token-input" />
          <input value={pageId} onChange={e => setPageId(e.target.value)} placeholder="Page ID (optional)"
            className="w-full h-11 px-4 rounded-xl bg-white/[0.05] border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-pink-500/40" data-testid="social-pageid-input" />
          <p className="text-white/30 text-xs italic">Simulated — no real API call to Meta.</p>
          <button data-testid="social-connect-confirm" onClick={connect} disabled={busy}
            className="w-full h-11 rounded-xl bg-gradient-to-r from-pink-500 to-pink-600 text-white font-bold text-sm disabled:opacity-50">
            {busy ? 'Connecting...' : 'Connect'}
          </button>
        </div>
      </div>
    </div>
  );
}
